/**
 * templateLabels — THE formatter layer for everything a template surface prints.
 *
 * Before this module every surface derived its own strings from the raw DTO at render
 * time: four naive `brand + " " + model` joins (so `iQOO` / `iQOO 15 5G` read
 * "iQOO iQOO 15 5G"), three separate private `humanize()` copies with different rules,
 * a canonical `PRODUCT_CATEGORIES` map the index never consulted ("Tpu Phone Case" in
 * the list vs "Phone case (TPU)" in the editor), a status pill rendered three different
 * ways, a fabricated "Version 1" on every row, and raw UUIDs in the Model/Style columns.
 *
 * Everything that turns template data into words lives here, so the customer Templates
 * page, the Editor → Templates table, the preview drawer, the cards and the catalog tree
 * cannot drift again.
 */
import type { ProductTemplate, TemplateStatus } from "@genz/platform";
import { enumLabel, titleCase } from "../format/labels.js";
import { PRODUCT_CATEGORIES } from "./catalogOptions.js";

/** The one "no value" glyph these surfaces print — never a blank cell, never an id. */
const EM_DASH = "—";

/** A bare identifier (case-style / phone-model UUID). Printing one per row made every
 *  list read as debug output, so every label below refuses to render it. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Title-case a slug for a human label: "tpu-clear" → "TPU Clear". Blank → "" (the
 *  callers below decide whether that becomes an em dash; folder-name callers must not
 *  get one). Replaces the three private copies in CustomerTemplates / funnel / BatchPanel.
 *
 *  The implementation moved to `format/labels.ts` in WS-B B-1 so the acronym table has ONE
 *  home; the name, the shape and the Title Case rule are unchanged — this is the source of
 *  the export folder tree (`batch/BatchPanel.tsx:54-58`) and its casing is frozen. */
export { titleCase as humanize };

/** Split into words, ignoring repeated separators. */
function words(s: string): string[] {
  return s.split(/\s+/).filter(Boolean);
}

/**
 * Drop a leading run of words that is immediately repeated: "iQOO iQOO 15 5G" →
 * "iQOO 15 5G", "Apple Watch Apple Watch Series 9" → "Apple Watch Series 9". This is the
 * client-side repair for the server's naive `f"{brand} {model}"` join, which is all the
 * list DTO gives us (it carries no separate brand field). Longest repeat wins.
 */
function dropRepeatedPrefix(label: string): string {
  const w = words(label);
  for (let k = Math.floor(w.length / 2); k >= 1; k--) {
    const head = w.slice(0, k).join(" ").toLowerCase();
    const next = w.slice(k, k * 2).join(" ").toLowerCase();
    if (head === next) return w.slice(k).join(" ");
  }
  return label;
}

/** Does `label` start with `brand` as a whole word (case-insensitive)? */
function startsWithBrand(label: string, brand: string): boolean {
  const b = words(brand).map((x) => x.toLowerCase());
  const l = words(label).map((x) => x.toLowerCase());
  return b.length > 0 && b.length <= l.length && b.every((x, i) => x === l[i]);
}

/**
 * The Model label — "<brand> <model>" with the brand deduped.
 *
 * Pass `brand` where the two fields are still separate (the phone-model catalog, the
 * Sync request list); pass only `model` for the server-resolved `display.model`, which
 * is already joined and may already carry the duplicate.
 */
export function modelLabel(m: { brand?: string | null; model?: string | null }): string {
  const brand = (m.brand ?? "").trim();
  const model = (m.model ?? "").trim();
  if (!model) return brand && !UUID_RE.test(brand) ? brand : EM_DASH;
  if (UUID_RE.test(model)) return EM_DASH;
  const joined = brand && !startsWithBrand(model, brand) ? `${brand} ${model}` : model;
  return dropRepeatedPrefix(joined);
}

/**
 * The Product/Category label. The canonical `PRODUCT_CATEGORIES` label wins (so the
 * index and the editor's Category dropdown agree); a value that already reads human is
 * kept verbatim; anything else is humanized. Blank → em dash.
 */
export function categoryLabel(key: string | null | undefined): string {
  const raw = (key ?? "").trim();
  if (!raw) return EM_DASH;
  const canonical = PRODUCT_CATEGORIES.find((c) => c.value.toLowerCase() === raw.toLowerCase());
  if (canonical) return canonical.label;
  if (/[A-Z]/.test(raw) || raw.includes(" ")) return raw;
  return titleCase(raw) || EM_DASH;
}

/** The Style label — the server-RESOLVED case-style label, never the `caseStyleId`. */
export function styleLabel(style: string | null | undefined): string {
  const raw = (style ?? "").trim();
  if (!raw || UUID_RE.test(raw)) return EM_DASH;
  return raw;
}

/** Human status label. The server lifecycle is Draft | Pending | Active | Archived |
 *  Rejected; a legacy `Approved` row (the server's state machine can no longer emit one)
 *  reads as Active, matching `saveStatusCopy`.
 *
 *  Anything else — a status a newer server grows — is never dropped from the list
 *  (`rowSet.test.ts`), so it goes through the SCREEN rule rather than being printed raw:
 *  "frozen" reads "Frozen", not a wire value beside "Pending approval" (review-1 m-1).
 *  The five known values are unchanged by that rule, which is why it is the same rule. */
export function statusLabel(status: TemplateStatus | null | undefined): string {
  if (!status) return EM_DASH;
  if (status === "Pending") return "Pending approval";
  if (status === "Approved") return "Active";
  return enumLabel(status);
}

/**
 * D-4 — the status VOCABULARY, in one place.
 *
 * `Approved` is gone: the cloud state machine can no longer emit it (AUD-1 F-03 / SYNTHESIS §6
 * C-3), so an option for it filtered to nothing while implying a state the server does not
 * have. It survives only as a union member so a legacy row still renders, and everywhere it
 * does it reads — and filters — as Active.
 *
 * `Rejected` and `Archived` fold into ONE "Needs attention" value: both mean "this template is
 * not going to render for anyone until a human does something", and two options that each
 * usually match zero rows is worse than one that sometimes matches some.
 *
 * WS-K6 spells the fold OUT. The chip row could carry it on the word "attention" alone,
 * because "Draft" and "Pending" stood beside it in a row of five; the Status dropdown that
 * replaced the chips is read one option at a time, so the option says which two statuses it
 * covers. It is still one VALUE — one checkbox, one chip, one click to undo — and `all` is the
 * identity case `facetMatches` implements, expressed in the facet model as an empty selection.
 */
export type StatusFacet = "all" | "Active" | "Draft" | "Pending" | "attention";

export const STATUS_FACETS: readonly { key: StatusFacet; label: string }[] = [
  { key: "all", label: "All" },
  { key: "Active", label: "Active" },
  { key: "Draft", label: "Draft" },
  { key: "Pending", label: "Pending" },
  { key: "attention", label: "Needs attention (rejected or archived)" },
];

/** Does this status read as "live"? Legacy `Approved` does — see `statusLabel`. */
export function isActiveStatus(status: TemplateStatus | null | undefined): boolean {
  return status === "Active" || status === "Approved";
}

/** Does a row with `status` belong under `facet`? */
export function facetMatches(facet: StatusFacet, status: TemplateStatus | null | undefined): boolean {
  switch (facet) {
    case "all": return true;
    case "Active": return isActiveStatus(status);
    case "Draft": return status === "Draft";
    case "Pending": return status === "Pending";
    case "attention": return status === "Rejected" || status === "Archived";
  }
}

/** Status → badge modifier class. One class per real status, so "awaiting review" and
 *  "rejected" are never the same amber pill. Legacy `Approved` shares Active's green. */
const STATUS_BADGE: Record<TemplateStatus, string> = {
  Draft: "badge--neutral",
  Pending: "badge--warn",
  Active: "badge--success",
  Approved: "badge--success",
  Archived: "badge--info",
  Rejected: "badge--danger",
};

/** The badge modifier class for a status (neutral for anything unknown). */
export function statusBadgeClass(status: TemplateStatus | null | undefined): string {
  return (status && STATUS_BADGE[status]) || "badge--neutral";
}

/**
 * The Brand label — the index tables' own column (D-3 iii).
 *
 * Resolution order, and it is deliberately short:
 *   1. `t.brand`, which WS-C resolves server-side.
 *   2. the phone-model catalog the page already loads, keyed by `phoneModelId`.
 *   3. an em dash.
 *
 * It NEVER guesses by splitting `display.model`. "Apple Watch Series 9" would yield "Apple"
 * and "Nothing Phone 3a" would yield "Nothing", but "OnePlus 6" vs "One Plus" is a coin flip
 * — and a wrong Brand column is worse than an empty one. The cards, the preview drawer and
 * every dropdown keep the single deduped `modelLabel()` string: they have one line to say it
 * in, the tables have two columns.
 */
export function brandOf(
  t: ProductTemplate,
  byId?: ReadonlyMap<string, { brand?: string | null }>,
): string {
  const server = (t.brand ?? "").trim();
  if (server && !UUID_RE.test(server)) return server;
  const catalog = (t.phoneModelId ? byId?.get(t.phoneModelId)?.brand ?? "" : "").trim();
  if (catalog && !UUID_RE.test(catalog)) return catalog;
  return EM_DASH;
}

/**
 * The status the ROW pill should report: the HEAD version's, falling back to the current
 * version's when no server sends the head pair yet.
 *
 * A template sitting on an active v1 with a v2 Draft above it belongs under the Draft chip —
 * reporting the active version is how a draft awaiting work became invisible from the list
 * (AUD-1 F-17 / AUD-6 IMP-14).
 */
export function headStatusOf(t: ProductTemplate): TemplateStatus | null | undefined {
  return t.headVersion?.status ?? t.currentVersion?.status;
}

/**
 * The row's second line when the head version is AHEAD of the active one — "v2 draft
 * pending" / "v3 awaiting approval" — and `""` otherwise.
 *
 * Only Draft and Pending earn a line: a head that is Active, Archived or Rejected IS the
 * current version's story, and a second line there would only repeat the pill.
 */
export function headSubline(t: ProductTemplate): string {
  const head = t.headVersion;
  if (!head) return "";
  const current = t.currentVersion?.versionNumber;
  if (typeof current === "number" && head.versionNumber <= current) return "";
  if (head.status === "Draft") return `v${head.versionNumber} draft pending`;
  if (head.status === "Pending") return `v${head.versionNumber} awaiting approval`;
  return "";
}

/**
 * The output-format label — "png" → "PNG", blank → an em dash.
 *
 * A file format is an acronym whatever the shared table happens to list, so this is upper
 * case rather than `enumLabel` (which would print "Tiff" for a format nobody seeded). It is
 * exported because the rule had THREE private copies — `TemplateListPage.rowMeta`,
 * `templateSubline` below and `Wizard.tsx`'s Format options — which is RC-3 in miniature
 * (review-1 m-3).
 */
export function outputFormatLabel(format: string | null | undefined): string {
  const raw = (format ?? "").trim();
  return raw ? raw.toUpperCase() : EM_DASH;
}

/** "Version 3" — or "Version —" when the number is genuinely unknown. */
export function versionLabel(n: number | null | undefined): string {
  return `Version ${typeof n === "number" ? n : EM_DASH}`;
}

/** "N views" (or an em dash when the template declares none). */
function viewsLabel(views: readonly string[] | null | undefined): string {
  const n = views?.length ?? 0;
  return n ? `${n} view${n === 1 ? "" : "s"}` : EM_DASH;
}

/**
 * The one template sub-line: "code · category · style · format · N views". Used by the
 * customer table/cards AND (behind the version) the admin table, so the same template
 * describes itself identically on every surface. The Model is deliberately absent —
 * both tables carry a dedicated Model column.
 */
export function templateSubline(t: ProductTemplate): string {
  const d = t.display;
  const parts = [
    categoryLabel(d?.category),
    styleLabel(d?.style),
    outputFormatLabel(d?.outputFormat),
    viewsLabel(d?.views),
  ];
  if (t.code) parts.unshift(t.code);
  return parts.join(" · ");
}
